import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link, useLocation } from "react-router-dom";
import "./styles/Login.css";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState("");
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isEmailLoading, setIsEmailLoading] = useState(false);

  const { signIn, signInWithGoogle, user, authError, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasRedirected = useRef(false); // Prevent multiple redirects

  // Handle redirect after successful authentication
  useEffect(() => {
    if (user && location.pathname === "/login" && !hasRedirected.current) {
      console.log("✅ User authenticated on login page, redirecting to dashboard");
      hasRedirected.current = true;
      
      // Use replace to avoid adding to history stack
      navigate("/dashboard", { replace: true });
    }
  }, [user, location.pathname, navigate]);

  // Reset redirect flag when user changes (logout scenario)
  useEffect(() => {
    if (!user) {
      hasRedirected.current = false;
    }
  }, [user]);

  useEffect(() => {
    if (authError) {
      setMessage(getErrorMessage(authError));
      setMessageType("error");
      setIsGoogleLoading(false);
      setIsEmailLoading(false);
    }
  }, [authError]);

  useEffect(() => {
    if (message && (email || password)) {
      setMessage(null);
      clearError();
    }
  }, [email, password, message, clearError]);

  const getErrorMessage = (error) => {
    if (error.includes("auth/user-not-found")) {
      return "No account found with this email address.";
    } else if (error.includes("auth/wrong-password")) {
      return "Incorrect password. Please try again.";
    } else if (error.includes("auth/invalid-email")) {
      return "Please enter a valid email address.";
    } else if (error.includes("auth/popup-closed-by-user")) {
      return "Sign-in was cancelled. Please try again.";
    } else if (error.includes("auth/popup-blocked")) {
      return "Please allow popups and try again.";
    } else if (error.includes("verification")) {
      return "This app is currently in testing mode. Please contact the developer for access.";
    }
    return "Authentication failed. Please try again.";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEmailLoading || isGoogleLoading) return;

    setMessage(null);
    setIsEmailLoading(true);
    clearError();

    try {
      await signIn(email, password);
      setMessage("Login successful! Redirecting...");
      setMessageType("success");
    } catch (error) {
      console.error("❌ Email login failed");
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    if (isGoogleLoading || isEmailLoading) return;

    try {
      setIsGoogleLoading(true);
      setMessage("Signing in with Google...");
      setMessageType("info");
      clearError();

      console.log("🔄 Initiating Google login...");
      const result = await signInWithGoogle();

      if (result?.user) {
        console.log("✅ Google login successful");
        setMessage("Login successful! Redirecting...");
        setMessageType("success");
        // Don't set loading to false here - let the redirect happen
      }
    } catch (error) {
      console.error("❌ Google login failed");
      setIsGoogleLoading(false);
    }
  };

  // If user is already authenticated, show loading state
  if (user && location.pathname === "/login") {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <img src="/logo192.png" alt="Fan Creator Logo" className="login-logo" />
            <h2>Redirecting to dashboard...</h2>
          </div>
          <div className="login-message success">
            Login successful! Please wait...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <img src="/logo192.png" alt="Fan Creator Logo" className="login-logo" />
          <h2>Welcome back!</h2>
        </div>

        {message && (
          <div className={`login-message ${messageType}`}>
            {message}
          </div>
        )}

        <div className="social-login">
          <button 
            className="google-login" 
            onClick={handleGoogleLogin}
            disabled={isGoogleLoading || isEmailLoading}
          >
            <img src="/google-icon.png" alt="Google" /> 
            {isGoogleLoading ? "Signing in with Google..." : "Sign in with Google"}
          </button>
        </div>

        <div className="separator"><span>or</span></div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-field-wrapper">
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="login-input"
              disabled={isGoogleLoading || isEmailLoading}
            />
          </div>

          <div className="form-field-wrapper">
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="login-input"
              disabled={isGoogleLoading || isEmailLoading}
            />
          </div>

          <div className="form-field-wrapper forgot-password">
            <Link to="/forgot-password">Forgot password?</Link>
          </div>

          <div className="form-field-wrapper">
            <button 
              type="submit" 
              className="login-button"
              disabled={isGoogleLoading || isEmailLoading}
            >
              {isEmailLoading ? "Signing in..." : "Log in"}
            </button>
          </div>
        </form>

        <div className="signup-link">
          No account yet? <Link to="/signup">Create account for free</Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
